import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiFile, FiFolder, FiUsers, FiDownload, FiEye, FiFileText, FiImage, FiUpload, FiSearch, FiMessageSquare, FiPlus, FiClock, FiTag, FiHardDrive, FiActivity, FiCheckCircle, FiAlertCircle, FiAlertTriangle, FiThumbsUp, FiThumbsDown, FiUser } from 'react-icons/fi';
import { AiOutlineFilePdf, AiOutlineFileWord, AiOutlineFileExcel, AiOutlineFileZip } from 'react-icons/ai';
import { BsFiletypeCsv, BsRobot } from 'react-icons/bs';
import { documentsAPI, foldersAPI, auditAPI, actionsAPI } from '../lib/api';
import useAuthStore from '../store/authStore';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/ToastContainer';

const Dashboard = () => {
  const profile = useAuthStore((state) => state.profile);
  const navigate = useNavigate();
  const { showInfo } = useToast();
  const previousStatsRef = useRef(null);

  const getFileIcon = (filename, mimeType) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    
    if (mimeType?.includes('pdf') || ext === 'pdf') {
      return <AiOutlineFilePdf className="w-6 h-6 text-red-500" />;
    }
    if (mimeType?.includes('word') || ['doc', 'docx'].includes(ext)) {
      return <AiOutlineFileWord className="w-6 h-6 text-blue-500" />;
    }
    if (mimeType?.includes('sheet') || mimeType?.includes('excel') || ['xls', 'xlsx'].includes(ext)) {
      return <AiOutlineFileExcel className="w-6 h-6 text-green-600" />;
    }
    if (ext === 'csv') {
      return <BsFiletypeCsv className="w-6 h-6 text-green-600" />;
    }
    if (mimeType?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
      return <FiImage className="w-6 h-6 text-purple-500" />;
    }
    if (mimeType?.includes('zip') || mimeType?.includes('compressed') || ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return <AiOutlineFileZip className="w-6 h-6 text-yellow-600" />;
    }
    if (mimeType?.includes('text') || ['txt', 'log', 'md'].includes(ext)) {
      return <FiFileText className="w-6 h-6 text-gray-500" />;
    }
    
    return <FiFile className="w-6 h-6 text-gray-400" />;
  };

  const { data: documents } = useQuery({
    queryKey: ['documents-recent'],
    queryFn: () => documentsAPI.getAll({ limit: 5 }),
  });

  const { data: folders } = useQuery({
    queryKey: ['folders-all'],
    queryFn: () => foldersAPI.getAll(),
  });

  // Get all documents for analytics (not just recent 5)
  const { data: allDocuments } = useQuery({
    queryKey: ['documents-all'],
    queryFn: () => documentsAPI.getAll({ limit: 1000, include_content: true }),
  });

  // Get recent activity from audit log — admin only (non-admins get 403)
  const { data: recentActivity } = useQuery({
    queryKey: ['audit-recent', profile?.id],
    queryFn: () => auditAPI.getLogs({ limit: 5, user_id: profile?.id }),
    enabled: !!profile?.id && profile?.role === 'admin',
  });

  // Get Me To Action stats with auto-refresh every 30 seconds
  const { data: actionStats } = useQuery({
    queryKey: ['my-action-stats'],
    queryFn: () => actionsAPI.getMyStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Detect changes in action stats and show notifications
  useEffect(() => {
    if (actionStats && previousStatsRef.current) {
      const prev = previousStatsRef.current;
      const curr = actionStats.data;

      // Check for new pending actions
      if (curr.pending > prev.pending) {
        const diff = curr.pending - prev.pending;
        showInfo(`You have ${diff} new action${diff > 1 ? 's' : ''} to review!`, 5000);
      }

      // Check for new approved actions
      if (curr.approved > prev.approved) {
        const diff = curr.approved - prev.approved;
        showInfo(`${diff} action${diff > 1 ? 's' : ''} ha${diff > 1 ? 've' : 's'} been approved!`, 5000);
      }

      // Check for new rejected actions
      if (curr.rejected > prev.rejected) {
        const diff = curr.rejected - prev.rejected;
        showInfo(`${diff} action${diff > 1 ? 's' : ''} ha${diff > 1 ? 've' : 's'} been rejected`, 5000);
      }

      // Check for new completed actions
      if (curr.completed > prev.completed) {
        const diff = curr.completed - prev.completed;
        showInfo(`${diff} action${diff > 1 ? 's' : ''} ha${diff > 1 ? 've' : 's'} been completed!`, 5000);
      }
    }

    // Update the reference with current stats
    if (actionStats) {
      previousStatsRef.current = actionStats.data;
    }
  }, [actionStats, showInfo]);

  // Calculate document analytics
  const allDocs = allDocuments?.data?.documents || [];
  const docsWithAI = allDocs.filter(doc => {
    // Handle both object (single metadata) and array (multiple metadata) formats
    if (Array.isArray(doc.document_ai_metadata)) {
      return doc.document_ai_metadata.length > 0 && doc.document_ai_metadata[0];
    }
    // If it's an object (single metadata), it exists
    return doc.document_ai_metadata && typeof doc.document_ai_metadata === 'object';
  });
  const totalSize = allDocs.reduce((sum, doc) => sum + (doc.file_size || 0), 0);

  // Calculate file type breakdown
  const fileTypeBreakdown = allDocs.reduce((acc, doc) => {
    const ext = doc.name?.split('.').pop()?.toLowerCase();
    const mime = doc.mime_type?.toLowerCase();

    let type = 'Other';
    if (mime?.includes('pdf') || ext === 'pdf') type = 'PDF';
    else if (mime?.includes('word') || ['doc', 'docx'].includes(ext)) type = 'Word';
    else if (mime?.includes('sheet') || mime?.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext)) type = 'Excel';
    else if (mime?.includes('image')) type = 'Image';

    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  // Collect all tags from user's own documents only
  const allTags = [];
  const userDocs = allDocs.filter(doc => doc.uploaded_by === profile?.id);
  userDocs.forEach(doc => {
    if (doc.tags && Array.isArray(doc.tags)) {
      allTags.push(...doc.tags);
    }
    // Handle AI metadata as both object and array
    const aiMetadata = Array.isArray(doc.document_ai_metadata)
      ? doc.document_ai_metadata[0]
      : doc.document_ai_metadata;
    if (aiMetadata?.suggested_tags && Array.isArray(aiMetadata.suggested_tags)) {
      allTags.push(...aiMetadata.suggested_tags);
    }
  });

  // Count tag occurrences
  const tagCounts = allTags.reduce((acc, tag) => {
    if (tag) {
      acc[tag] = (acc[tag] || 0) + 1;
    }
    return acc;
  }, {});

  // Get top 15 tags
  const topTags = Object.entries(tagCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15)
    .map(([tag, count]) => ({ tag, count }));

  const stats = [
    {
      name: 'Total Documents',
      value: allDocs.length || 0,
      icon: FiFile,
      color: 'bg-blue-500',
    },
    {
      name: 'Active Folders',
      value: folders?.data?.folders?.length || 0,
      icon: FiFolder,
      color: 'bg-green-500',
    },
    {
      name: 'AI Processed',
      value: docsWithAI.length || 0,
      icon: BsRobot,
      color: 'bg-purple-500',
    },
    {
      name: 'Total Storage',
      value: `${(totalSize / 1024 / 1024).toFixed(1)} MB`,
      icon: FiHardDrive,
      color: 'bg-orange-500',
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Welcome back, {profile?.full_name}</p>
      </div>

      {/* Me To Action Header Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
        <button
          onClick={() => navigate('/me-to-action?status=pending')}
          className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-blue-600">{actionStats?.data?.pending || 0}</p>
            </div>
            <FiClock className="w-8 h-8 text-blue-600" />
          </div>
        </button>

        <button
          onClick={() => navigate('/me-to-action?priority=urgent')}
          className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Urgent</p>
              <p className="text-2xl font-bold text-orange-600">{actionStats?.data?.byPriority?.urgent || 0}</p>
            </div>
            <FiAlertTriangle className="w-8 h-8 text-orange-600" />
          </div>
        </button>

        <button
          onClick={() => navigate('/me-to-action')}
          className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Overdue</p>
              <p className="text-2xl font-bold text-red-600">{actionStats?.data?.overdue || 0}</p>
            </div>
            <FiAlertCircle className="w-8 h-8 text-red-600" />
          </div>
        </button>

        <button
          onClick={() => navigate('/me-to-action?status=approved')}
          className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-emerald-600">{actionStats?.data?.approved || 0}</p>
            </div>
            <FiThumbsUp className="w-8 h-8 text-emerald-600" />
          </div>
        </button>

        <button
          onClick={() => navigate('/me-to-action?status=rejected')}
          className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-rose-600">{actionStats?.data?.rejected || 0}</p>
            </div>
            <FiThumbsDown className="w-8 h-8 text-rose-600" />
          </div>
        </button>

        <button
          onClick={() => navigate('/me-to-action?assigned=me')}
          className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Assign To Me</p>
              <p className="text-2xl font-bold text-indigo-600">{actionStats?.data?.assigned_to_me || 0}</p>
            </div>
            <FiUser className="w-8 h-8 text-indigo-600" />
          </div>
        </button>

        <button
          onClick={() => navigate('/me-to-action?status=completed')}
          className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{actionStats?.data?.completed || 0}</p>
            </div>
            <FiCheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </button>
      </div>

      {/* Quick Actions */}
      <div className="card mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FiActivity className="w-5 h-5" />
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/documents')}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-gray-200 hover:border-primary-500 hover:bg-primary-50 transition-all group"
          >
            <div className="p-3 bg-blue-100 text-blue-600 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors">
              <FiUpload className="w-6 h-6" />
            </div>
            <span className="text-sm font-medium text-gray-700 group-hover:text-primary-700">Upload Document</span>
          </button>

          <button
            onClick={() => navigate('/folders')}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-gray-200 hover:border-primary-500 hover:bg-primary-50 transition-all group"
          >
            <div className="p-3 bg-green-100 text-green-600 rounded-lg group-hover:bg-green-600 group-hover:text-white transition-colors">
              <FiPlus className="w-6 h-6" />
            </div>
            <span className="text-sm font-medium text-gray-700 group-hover:text-primary-700">Create Folder</span>
          </button>

          <button
            onClick={() => navigate('/search')}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-gray-200 hover:border-primary-500 hover:bg-primary-50 transition-all group"
          >
            <div className="p-3 bg-purple-100 text-purple-600 rounded-lg group-hover:bg-purple-600 group-hover:text-white transition-colors">
              <FiSearch className="w-6 h-6" />
            </div>
            <span className="text-sm font-medium text-gray-700 group-hover:text-primary-700">Search</span>
          </button>

          <button
            onClick={() => navigate('/chatbot')}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-gray-200 hover:border-primary-500 hover:bg-primary-50 transition-all group"
          >
            <div className="p-3 bg-orange-100 text-orange-600 rounded-lg group-hover:bg-orange-600 group-hover:text-white transition-colors">
              <FiMessageSquare className="w-6 h-6" />
            </div>
            <span className="text-sm font-medium text-gray-700 group-hover:text-primary-700">AI Assistant</span>
          </button>
        </div>
      </div>



      {/* Tag Cloud */}
      {topTags.length > 0 && (
        <div className="card mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FiTag className="w-5 h-5" />
            My Created Tags
          </h2>
          <div className="flex flex-wrap gap-2">
            {topTags.map(({ tag, count }) => (
              <button
                key={tag}
                onClick={() => navigate(`/search?q=${encodeURIComponent(tag)}`)}
                className="px-3 py-2 rounded-lg bg-primary-50 text-primary-700 border border-primary-200 hover:bg-primary-100 transition-colors text-sm font-medium"
                title={`${count} document${count > 1 ? 's' : ''}`}
              >
                {tag}
                <span className="ml-2 text-xs text-primary-500">({count})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Recent Activity Timeline */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FiClock className="w-5 h-5" />
            Recent Activity
          </h2>
          <div className="space-y-3">
            {recentActivity?.data?.data?.map((activity, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className={`p-2 rounded-lg ${
                  activity.action === 'CREATE' ? 'bg-green-100 text-green-600' :
                  activity.action === 'DELETE' ? 'bg-red-100 text-red-600' :
                  activity.action === 'UPDATE' ? 'bg-blue-100 text-blue-600' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {activity.action === 'CREATE' && <FiPlus className="w-4 h-4" />}
                  {activity.action === 'DELETE' && <FiFile className="w-4 h-4" />}
                  {activity.action === 'UPDATE' && <FiFile className="w-4 h-4" />}
                  {activity.action === 'LOGIN' && <FiUsers className="w-4 h-4" />}
                  {!['CREATE', 'DELETE', 'UPDATE', 'LOGIN'].includes(activity.action) && <FiActivity className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {activity.user_profiles?.full_name || 'Unknown User'}
                  </p>
                  <p className="text-xs text-gray-600">
                    {activity.action} {activity.resource_type}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(activity.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
            {(!recentActivity?.data?.data || recentActivity.data.data.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>
            )}
          </div>
        </div>

        {/* Document Analytics */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FiFile className="w-5 h-5" />
            Document Analytics
          </h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">File Types</h3>
              <div className="space-y-2">
                {Object.entries(fileTypeBreakdown).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {type === 'PDF' && <AiOutlineFilePdf className="w-5 h-5 text-red-500" />}
                      {type === 'Word' && <AiOutlineFileWord className="w-5 h-5 text-blue-500" />}
                      {type === 'Excel' && <AiOutlineFileExcel className="w-5 h-5 text-green-600" />}
                      {type === 'Image' && <FiImage className="w-5 h-5 text-purple-500" />}
                      {type === 'Other' && <FiFile className="w-5 h-5 text-gray-400" />}
                      <span className="text-sm text-gray-700">{type}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-2">AI Processing Status</h3>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Processed</span>
                <span className="text-sm font-semibold text-purple-600">{docsWithAI.length} / {allDocs.length}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all"
                  style={{ width: `${allDocs.length > 0 ? (docsWithAI.length / allDocs.length) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Documents */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Documents</h2>
        <div className="space-y-3">
          {documents?.data?.documents?.slice(0, 5).map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors border border-gray-200 cursor-pointer"
              onClick={() => navigate(`/documents/${doc.id}`)}
            >
              {getFileIcon(doc.name, doc.mime_type)}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900 truncate">{doc.name}</p>
                  {(doc.document_ai_metadata && (Array.isArray(doc.document_ai_metadata) ? doc.document_ai_metadata.length > 0 : true)) && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-100 text-purple-700" title="AI Processed">
                      <BsRobot className="w-3 h-3" />
                    </span>
                  )}
                </div>
                <div className="flex items-center flex-wrap gap-2 mt-1">
                  <span className="text-xs text-gray-500">
                    {new Date(doc.created_at).toLocaleDateString()} • {(doc.file_size / 1024).toFixed(2)} KB
                  </span>
                  {(() => {
                    const aiMetadata = Array.isArray(doc.document_ai_metadata) ? doc.document_ai_metadata[0] : doc.document_ai_metadata;
                    return aiMetadata?.document_type && (
                      <span className="text-xs px-2 py-1 bg-primary-100 text-primary-700 rounded">
                        {aiMetadata.document_type}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/documents/${doc.id}`); }}
                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="View Details"
                >
                  <FiEye className="w-4 h-4" />
                </button>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const response = await documentsAPI.download(doc.id);
                      const blob = response.data;
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', doc.name);
                      document.body.appendChild(link);
                      link.click();
                      setTimeout(() => { link.remove(); window.URL.revokeObjectURL(url); }, 100);
                    } catch (error) {
                      console.error('Download error:', error);
                      alert('Failed to download document');
                    }
                  }}
                  className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                  title="Download"
                >
                  <FiDownload className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
